import React from 'react'
import moment from 'moment'

import { LoaderIcon } from '@/app/features/icons/Loader'
import { faFileCertificateSolid, FaIcon } from '@/lib/fa'
import { getAttestationName } from '@/app/services/attestation/attestationNameResolver'
import { getAttestationType } from '@/app/services/attestation/attestationTypeResolver'
import AttestationsService from '@/modules/drivers/services/AttestationsService'
import type { AttestationRow } from '@/modules/drivers/tabs/activities/modals/components/AttestationRowItem'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { COLORS } from '../CalendarDayItem'

const formatDateTime = (value: string | null | undefined): string =>
  value ? moment(value).format('YYYY-MM-DD HH:mm') : '-'

const formatDate = (value: string | null | undefined): string =>
  value ? moment(value).format('YYYY-MM-DD') : '-'

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
    <span className="tw-text-xs tw-text-content-base/70">{label}</span>
    <span className="tw-text-xs tw-font-medium tw-text-content-base">
      {value}
    </span>
  </div>
)


const CalendarDayInfoAttestations = ({
  companyId,
  driverId,
  date,
  attestationType,
}: {
  companyId: string
  driverId: string | number
  date: string
  attestationType?: number
}) => {
  const { t, tSafe } = useLocalize()
  const { getDayAttestations } = AttestationsService()
  const [attestations, setAttestations] = React.useState<AttestationRow[]>([])
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    let isActive = true
    const fetchData = async () => {
      setIsLoading(true)
      const data = await getDayAttestations({ companyId, driverId, date })
      if (!isActive) return
      setAttestations(Array.isArray(data) ? data : [])
      setIsLoading(false)
    }
    fetchData()
    return () => {
      isActive = false
    }
  }, [companyId, driverId, date])

  const typeLabel = (type?: number) =>
    type ? getAttestationName(getAttestationType(type), t) : '-'

  return (
    <div className="tw-space-y-1.5">
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <FaIcon
          icon={faFileCertificateSolid}
          className="tw-h-3 tw-w-3"
          style={{ color: COLORS.attestationDay }}
        />
        <p className="tw-text-xs tw-font-semibold tw-uppercase tw-text-content-base/80">
          {tSafe('attestation.day-section-title', 'Zaświadczenie')}
        </p>
      </div>

      {isLoading ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-2">
          <LoaderIcon className="tw-h-4 tw-w-4 tw-animate-spin tw-text-primary" />
        </div>
      ) : attestations.length === 0 ? (
  
        <p className="tw-text-xs tw-font-medium tw-text-content-base">
          {typeLabel(attestationType)}
        </p>
      ) : (
        <div className="tw-space-y-1.5">
          {attestations.map((attestation) => (
            <div
              key={attestation.id}
              className="tw-space-y-0.5 tw-rounded tw-bg-base-200/60 tw-p-2"
            >
              <p className="tw-text-xs tw-font-semibold tw-text-content-base">
                {typeLabel(attestation.attestationType)}
              </p>
              <DetailRow
                label={t('common.from')}
                value={formatDateTime(attestation.startTime)}
              />
              <DetailRow
                label={t('common.to')}
                value={formatDateTime(attestation.endTime)}
              />
              {(attestation.activityStartTime || attestation.activityEndTime) && (
                <DetailRow
                  label={tSafe('attestation.activity-range', 'Aktywność')}
                  value={`${formatDateTime(attestation.activityStartTime)} - ${formatDateTime(attestation.activityEndTime)}`}
                />
              )}
              {attestation.issuer && (
                <DetailRow
                  label={tSafe('attestation.issuer', 'Wystawca')}
                  value={
                    attestation.position
                      ? `${attestation.issuer} (${attestation.position})`
                      : attestation.issuer
                  }
                />
              )}
              {attestation.createdDate && (
                <DetailRow
                  label={t('businessTrip.creation-date')}
                  value={formatDate(attestation.createdDate)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CalendarDayInfoAttestations
